import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowDownRight, ArrowUpRight, ArrowLeft, Download, Filter,
  Loader2, RefreshCw, Wallet as WalletIcon,
} from "lucide-react";

/*  /vendor/wallet/ledger  —  Phase 19.A5

    Canonical, filterable, paginated wallet ledger backed by the new
    db.wallet_ledger collection. Every wallet movement (top-up,
    admin adjustment, promo grant, campaign fee, listing duration,
    featured slot, retargeting, opening_balance, reconciliation …)
    appears here as a single row with amount, bucket, direction and
    a friendly reason label.

    Filters:  bucket · direction · reason · date range
    Actions:  paginate · export CSV
*/

const rupees = (paise) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency", currency: "INR", minimumFractionDigits: 2,
  }).format(Number(paise || 0) / 100);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  } catch {
    return iso;
  }
};

const PAGE_SIZE = 25;

export default function WalletLedgerPage() {
  const [params, setParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const bucket = params.get("bucket") || "";
  const direction = params.get("direction") || "";
  const reason = params.get("reason") || "";
  const dateFrom = params.get("date_from") || "";
  const dateTo = params.get("date_to") || "";
  const page = Math.max(1, Number(params.get("page") || "1"));
  const skip = (page - 1) * PAGE_SIZE;

  const load = useCallback(async () => {
    setErr(null);
    setRefreshing(true);
    try {
      const query = {};
      if (bucket) query.bucket = bucket;
      if (direction) query.direction = direction;
      if (reason) query.reason = reason;
      if (dateFrom) query.date_from = dateFrom;
      if (dateTo) query.date_to = dateTo;
      query.limit = PAGE_SIZE;
      query.skip = skip;
      const qs = new URLSearchParams(query).toString();
      const res = await api.walletLedger(qs);
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Failed to load ledger");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [bucket, direction, reason, dateFrom, dateTo, skip]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") next.delete(k);
      else next.set(k, String(v));
    });
    // Reset to page 1 on any filter change (unless "page" itself is the patch).
    if (!("page" in patch)) next.delete("page");
    setParams(next, { replace: true });
  };

  const clearFilters = () => setParams(new URLSearchParams(), { replace: true });

  const totalPages = useMemo(
    () => (data ? Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE)) : 1),
    [data]
  );

  const exportCsvHref = useMemo(() => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const q = new URLSearchParams();
    if (bucket) q.set("bucket", bucket);
    if (direction) q.set("direction", direction);
    if (reason) q.set("reason", reason);
    if (dateFrom) q.set("date_from", dateFrom);
    if (dateTo) q.set("date_to", dateTo);
    return `${base}/api/wallet/ledger/export.csv${q.toString() ? `?${q}` : ""}`;
  }, [bucket, direction, reason, dateFrom, dateTo]);

  return (
    <div className="space-y-6" data-testid="vendor-wallet-ledger">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <Link
              to="/vendor/wallet"
              className="inline-flex items-center gap-1 text-sm font-semibold text-neutral-600 hover:text-black"
              data-testid="ledger-back-link"
            >
              <ArrowLeft className="w-4 h-4" /> Wallet
            </Link>
          </div>
          <h1 className="text-2xl md:text-3xl font-black tracking-tight mt-1">
            Transaction ledger
          </h1>
          <p className="text-sm text-neutral-600">
            Every credit and debit against your wallet — canonical, immutable, exportable.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={load}
            disabled={refreshing}
            data-testid="ledger-refresh-btn"
          >
            {refreshing
              ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              : <RefreshCw className="w-4 h-4 mr-1" />}
            Refresh
          </Button>
          <a
            href={exportCsvHref}
            className="inline-flex items-center justify-center gap-1 rounded-md bg-black text-white text-sm font-semibold px-3 py-2 hover:bg-neutral-800"
            data-testid="ledger-export-csv-btn"
          >
            <Download className="w-4 h-4" /> Export CSV
          </a>
        </div>
      </div>

      {/* Balance summary */}
      {data?.balances && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card className="p-4 border-2 border-black" data-testid="ledger-total-card">
            <div className="text-xs font-bold uppercase tracking-wider text-neutral-500">Total balance</div>
            <div className="text-3xl font-black tabular-nums mt-1">
              {rupees(data.balances.total_paise)}
            </div>
          </Card>
          <Card className="p-4" data-testid="ledger-real-card">
            <div className="text-xs font-bold uppercase tracking-wider text-neutral-500">Purchased</div>
            <div className="text-2xl font-black tabular-nums mt-1">
              {rupees(data.balances.real_paise)}
            </div>
            <div className="text-xs text-neutral-500 mt-1">Funded by your top-ups</div>
          </Card>
          <Card className="p-4 bg-[color:var(--graffiti-yellow,#F5A800)]/10" data-testid="ledger-promo-card">
            <div className="text-xs font-bold uppercase tracking-wider text-neutral-500">Promotional</div>
            <div className="text-2xl font-black tabular-nums mt-1">
              {rupees(data.balances.promo_paise)}
            </div>
            <div className="text-xs text-neutral-500 mt-1">Spent first · granted by CityDooh</div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-neutral-500" />
          <div className="text-sm font-semibold">Filter</div>
          <button
            type="button"
            onClick={clearFilters}
            className="ml-auto text-xs font-semibold text-neutral-600 underline underline-offset-2 hover:text-black"
            data-testid="ledger-clear-filters-btn"
          >
            Clear
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <Label className="text-xs">Bucket</Label>
            <Select
              value={bucket || "all"}
              onValueChange={(v) => setFilter({ bucket: v === "all" ? "" : v })}
            >
              <SelectTrigger data-testid="ledger-filter-bucket">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All buckets</SelectItem>
                <SelectItem value="real">Purchased (real)</SelectItem>
                <SelectItem value="promo">Promotional</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Direction</Label>
            <Select
              value={direction || "all"}
              onValueChange={(v) => setFilter({ direction: v === "all" ? "" : v })}
            >
              <SelectTrigger data-testid="ledger-filter-direction">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All directions</SelectItem>
                <SelectItem value="credit">Credits only</SelectItem>
                <SelectItem value="debit">Debits only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Reason</Label>
            <Select
              value={reason || "all"}
              onValueChange={(v) => setFilter({ reason: v === "all" ? "" : v })}
            >
              <SelectTrigger data-testid="ledger-filter-reason">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All reasons</SelectItem>
                {Object.entries(data?.reason_labels || {}).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs" htmlFor="date-from">From</Label>
            <Input
              id="date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setFilter({ date_from: e.target.value })}
              data-testid="ledger-filter-date-from"
            />
          </div>
          <div>
            <Label className="text-xs" htmlFor="date-to">To</Label>
            <Input
              id="date-to"
              type="date"
              value={dateTo}
              onChange={(e) => setFilter({ date_to: e.target.value })}
              data-testid="ledger-filter-date-to"
            />
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ledger-table">
            <thead className="bg-neutral-50 text-neutral-600 uppercase text-xs tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Reason</th>
                <th className="px-4 py-3 text-left">Bucket</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3 text-left">Reference</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading && Array.from({ length: 6 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: 5 }).map((__, j) => (
                    <td key={j} className="px-4 py-3">
                      <Skeleton className="h-4 w-full" />
                    </td>
                  ))}
                </tr>
              ))}
              {!loading && err && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-red-600" data-testid="ledger-error">
                    {String(err)}
                  </td>
                </tr>
              )}
              {!loading && !err && (data?.items || []).length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-neutral-500" data-testid="ledger-empty">
                    No wallet activity found for the selected filters.
                  </td>
                </tr>
              )}
              {!loading && !err && (data?.items || []).map((row) => {
                const isCredit = row.direction === "credit";
                const amt = row.amount_paise || 0;
                return (
                  <tr key={row.id} className="hover:bg-neutral-50" data-testid={`ledger-row-${row.id}`}>
                    <td className="px-4 py-3 whitespace-nowrap text-neutral-700">
                      {fmtDate(row.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{row.reason_label}</div>
                      {row.metadata?.reason_note ? (
                        <div className="text-xs text-neutral-500 mt-0.5">{row.metadata.reason_note}</div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      {row.bucket === "promo" ? (
                        <Badge variant="secondary" className="bg-[color:var(--graffiti-yellow,#F5A800)]/20 text-black">
                          Promo
                        </Badge>
                      ) : (
                        <Badge variant="outline">Real</Badge>
                      )}
                    </td>
                    <td className={`px-4 py-3 text-right tabular-nums font-black ${isCredit ? "text-emerald-700" : "text-red-700"}`}>
                      <span className="inline-flex items-center gap-1 justify-end">
                        {isCredit ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                        {isCredit ? "+" : "−"}{rupees(amt)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-neutral-500">
                      {row.razorpay_payment_id
                        ? <span>Razorpay <span className="font-mono">{row.razorpay_payment_id}</span></span>
                        : row.reference_id
                          ? <span className="font-mono">{row.reference_id}</span>
                          : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!loading && !err && data?.total > PAGE_SIZE && (
          <div className="flex items-center justify-between p-3 border-t">
            <div className="text-xs text-neutral-500">
              Page <b>{page}</b> of <b>{totalPages}</b> · Showing {(data.items || []).length} of {data.total}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline" size="sm"
                onClick={() => setFilter({ page: Math.max(1, page - 1) })}
                disabled={page <= 1}
                data-testid="ledger-prev-page"
              >
                Previous
              </Button>
              <Button
                variant="outline" size="sm"
                onClick={() => setFilter({ page: page + 1 })}
                disabled={page >= totalPages}
                data-testid="ledger-next-page"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
